import React, { useState, useEffect, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit2, FiTrash2, FiPlus, FiX, FiInfo } = FiIcons;

const ManageReleaseNotes = () => {
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentNote, setCurrentNote] = useState(null);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: function() {
          const range = this.quill.getSelection();
          const value = prompt('Bitte geben Sie die Bild-URL ein:');
          if(value) {
            this.quill.insertEmbed(range.index, 'image', value, 'user');
          }
        }
      }
    }
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align',
    'link', 'image'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await apiService.getReleaseNotes({ sort: 'date', order: 'desc' });
      if (res.status === 'success') {
        const sorted = res.data.sort((a, b) => new Date(b.date) - new Date(a.date));
        setReleaseNotes(sorted);
      }
    } catch (error) {
      console.error('Error fetching release notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentNote) {
        await apiService.updateReleaseNote(currentNote.id, { title, description, date });
      } else {
        await apiService.createReleaseNote({ title, description, date });
      }
      fetchData();
      closeModal();
    } catch (error) {
      console.error('Error saving release note:', error);
      alert('Fehler beim Speichern der Release Note');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie diese Release Note wirklich löschen?')) {
      try {
        await apiService.deleteReleaseNote(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting release note:', error);
        alert('Fehler beim Löschen der Release Note');
      }
    }
  };

  const openModal = (note = null) => {
    if (note) {
      setCurrentNote(note);
      setTitle(note.title);
      setDescription(note.description);
      setDate(note.date);
    } else {
      setCurrentNote(null);
      setTitle('');
      setDescription('');
      setDate(new Date().toISOString().split('T')[0]); 
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentNote(null);
    setTitle('');
    setDescription('');
    setDate('');
  };

  if (loading) return <div>Laden...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Release Notes verwalten</h2>
        <button
          onClick={() => openModal()}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="mr-2" /> Neue Note
        </button>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {releaseNotes.map((note) => (
            <li key={note.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
              <div className="flex-1 min-w-0 mr-4">
                <div className="flex items-center mb-1">
                  <h3 className="text-lg font-medium text-gray-900 truncate mr-3">{note.title}</h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {note.date}
                  </span>
                </div>
                <div 
                  className="text-sm text-gray-500 line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: note.description }}
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => openModal(note)}
                  className="text-indigo-600 hover:text-indigo-900"
                  title="Bearbeiten"
                >
                  <SafeIcon icon={FiEdit2} className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(note.id)}
                  className="text-red-600 hover:text-red-900"
                  title="Löschen"
                >
                  <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                </button>
              </div>
            </li>
          ))}
          {releaseNotes.length === 0 && (
            <li className="px-6 py-4 text-center text-gray-500">Keine Release Notes vorhanden</li>
          )}
        </ul>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 my-8 flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium">
                {currentNote ? 'Release Note bearbeiten' : 'Neue Release Note'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-500">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <form id="noteForm" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                      <input
                        type="text"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="z.B. Version 1.2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Datum</label>
                      <input
                        type="date"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung (HTML)</label>
                    <div className="bg-white">
                      <ReactQuill 
                        theme="snow" 
                        value={description} 
                        onChange={setDescription}
                        modules={modules}
                        formats={formats}
                        className="h-64 mb-12"
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t bg-gray-50 rounded-b-lg">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                form="noteForm"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageReleaseNotes;